import { MutableRefObject, useEffect, useRef } from "react"
import { NextRouter } from "next/router"
import { SuiteNumber } from "@ssc-booking/api-contracts"
import * as O from "fp-ts/Option"
import * as E from "fp-ts/Either"
import * as F from "fp-ts/function"
import * as RF from "fp-ts/Refinement"
import { decode } from "@ssc-booking/functional-support"
import { useActor } from "@xstate/react"
import { match } from "ts-pattern"
import { decodeGuests } from "../../../_common/guests-encoding"
import { getFromNormalQuery, NormalQueryParam, removeQueryParam, replaceUrl } from "../../../_common/routing"
import { FareCardKey } from "../read-models/fare-card"
import { decodeGateway } from "../../../_common/gateway-encoding"
import { decodeAirFare } from "../../../_common/airfare-encoding"
import { decodeVs } from "../../../_common/vs-encoding"
import { ECommerceService } from "../machines/ecommerce-machine/e-commerce-machine"
import { decodeSuite } from "../../../_common/suite-encoding"

type RestoreStatus = "RESTORED" | "IN_PROGRESS" | "INIT"
type ParamStateValue = NotSetParamStateValue | SetParamStateValue
type NotSetParamStateValue = { status: RestoreStatus; qsValue: undefined }
type SetParamStateValue = { status: RestoreStatus; qsValue: string }
type CheckoutManagedByStateMachine = Exclude<NormalQueryParam, "checkout">
type ParamsState = Record<CheckoutManagedByStateMachine, ParamStateValue>

export const useHandleBrowserRefreshV4 = (
    isRestoreComplete: MutableRefObject<boolean>,
    router: NextRouter,
    ecommerce: ECommerceService,
): void => {
    const [, send] = useActor(ecommerce)

    const guests = getFromNormalQuery(router, "guests")
    const vs = getFromNormalQuery(router, "vs")
    const fare = getFromNormalQuery(router, "fare")
    const airFare = getFromNormalQuery(router, "airfare")
    const gateway = getFromNormalQuery(router, "gateway")
    const suiteCategory = getFromNormalQuery(router, "suite")
    const suiteNumber = getFromNormalQuery(router, "suite-number")

    const restoreParamRef = useRef<ParamsState>({
        guests: initParam(guests),
        vs: initParam(vs),
        fare: initParam(fare),
        airfare: initParam(airFare),
        gateway: initParam(gateway),
        suite: initParam(suiteCategory),
        "suite-number": initParam(suiteNumber),
        modal: initParam(getFromNormalQuery(router, "modal")),
    })

    useEffect(() => {
        ecommerce.onTransition((state) => {
            if (isRestoreComplete.current) return

            restoreParam(restoreParamRef, "guests", (guests) => {
                if (!state.matches("flow.nothingSelectedV4")) return

                send({ type: "CHANGE_GUESTS", guests: decodeGuests(guests) })
                return "RESTORED"
            })

            restoreParam(restoreParamRef, "vs", (vs) => {
                if (!state.matches("flow.nothingSelectedV4")) return

                send({ type: "CHANGE_VS_MEMBER", maybeVsMember: decodeVs(vs) })
                return "RESTORED"
            })

            restoreParam(restoreParamRef, "suite", (suiteCategory) => {
                if (!state.matches("flow.nothingSelectedV4.suiteCategorySelectable")) return

                return F.pipe(
                    decodeSuite(suiteCategory),
                    O.map(({ suiteCategory, source }) =>
                        match(source)
                            .with("standard", () => send({ type: "SELECT_SUITE_CATEGORY", suiteCategory }))
                            .with("quote", () => {
                                // NOTE: we don't support selecting a quote suite category
                                replaceUrl(router, removeQueryParam(location.href, "suite"))
                            })
                            .exhaustive(),
                    ),
                    F.constant("RESTORED"),
                )
            })

            restoreParam(restoreParamRef, "fare", (fare) => {
                if (!state.matches("flow.suiteCategorySelectedV4")) return

                return F.pipe(
                    FareCardKey.decode(fare),
                    E.map((fare) => send({ type: "SELECT_FARE", key: fare })),
                    F.constant("RESTORED"),
                )
            })

            restoreParam(restoreParamRef, "airfare", (airFare) => {
                if (!state.matches("flow.suiteCategorySelectedV4")) return

                return F.pipe(
                    decodeAirFare(airFare),
                    O.foldW(
                        () => send(notifyInvalidParameter("airfare", airFare)),
                        ({ key, airClass }) => send({ type: "CHANGE_AIR_CLASS", key, airClass }),
                    ),
                    F.constant("RESTORED"),
                )
            })

            restoreParam(restoreParamRef, "gateway", (gateway) => {
                if (!state.matches("flow.suiteCategorySelectedV4")) return

                return F.pipe(
                    decodeGateway(gateway),
                    O.map(({ key, gateway }) => send({ type: "CHANGE_GATEWAY", key, gatewayCode: gateway })),
                    F.constant("RESTORED"),
                )
            })

            restoreParam(restoreParamRef, "modal", () => {
                replaceUrl(router, removeQueryParam(location.href, "modal"))
                return "RESTORED"
            })

            restoreParam(
                restoreParamRef,
                "suite-number",
                byStatus(
                    (suiteNumber) => {
                        if (state.context.preferredSuiteNumber === undefined) {
                            return F.pipe(
                                suiteNumber.toUpperCase(),
                                decode(SuiteNumber.strictType),
                                E.fold(
                                    () => send(notifyInvalidParameter("suite-number", suiteNumber)),
                                    (suiteNumber) => send({ type: "SET_PREFERRED_SUITE_NUMBER", suiteNumber }),
                                ),
                                F.constant("IN_PROGRESS"),
                            )
                        }
                    },
                    () => {
                        if (
                            !state.matches("flow.fareSelectedV4.suiteNumberSelectable") ||
                            state.context.preferredSuiteNumber === undefined
                        )
                            return

                        send({ type: "SELECT_SUITE_NUMBER", suiteNumber: state.context.preferredSuiteNumber })
                        return "RESTORED"
                    },
                ),
            )

            isRestoreComplete.current =
                Object.values(restoreParamRef.current).every(alreadyRestoredOrNotSet) ||
                state.matches("flow.checkout.validated") ||
                state.matches("flow.readyToCheckout")
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
}

const initParam = (qsValue: string | undefined): ParamStateValue => ({ status: "INIT", qsValue })

type RestoredParamStateValue = Omit<ParamStateValue, "status"> & { status: "RESTORED" }
const alreadyRestored = (x: ParamStateValue): x is RestoredParamStateValue => x.status === "RESTORED"
const isSet = (x: ParamStateValue): x is SetParamStateValue => x.qsValue !== undefined
const alreadyRestoredOrNotSet = F.pipe(alreadyRestored, RF.or(RF.not(isSet)))

type RestoreFn = (qsValue: string, current: RestoreStatus) => RestoreStatus | undefined
const restoreParam = (
    ref: MutableRefObject<ParamsState>,
    paramKey: CheckoutManagedByStateMachine,
    fn: RestoreFn,
): void => {
    const param = ref.current[paramKey]
    if (alreadyRestoredOrNotSet(param)) return

    ref.current[paramKey].status = fn(param.qsValue, param.status) ?? ref.current[paramKey].status
}

const notifyInvalidParameter = (parameterName: string, value: unknown) => ({
    type: "NOTIFY" as const,
    origin: "url-to-state",
    kind: "invalidParameter" as const,
    info: { parameterName, value },
})

const byStatus =
    (init: RestoreFn, inProgress: RestoreFn = F.constUndefined): RestoreFn =>
    (qsValue, current) => {
        if (current === "INIT") return init(qsValue, current)
        if (current === "IN_PROGRESS") return inProgress(qsValue, current)
    }
